const { Route, DemandMVPOfferManagementRoute } = require('./routes');

/**
 * @readonly
 * @enum {string} Roles
 */
const Roles = {
  DemandUser: 'Demand User',
  ExternalUser: 'External User',
  Growth: 'Growth',
  InternalUser: 'Internal User',
  Scale: 'Scale',
  SelfService: 'Self Service',
  Starter: 'Starter',
  SupplyUser: 'Supply User',
};

/**
 * @readonly
 * @enum {string} RouteGuards
 */
const RouteGuards = {
  MigrationTrial: 'check_user_migration_trial',
  OnboardingFlow: 'check_onboarding_flow',
  PlanTrial: 'check_user_trial',
  RestrictCRM: 'restrict_crm',
};

/**x
 * @typedef {Object} UserMeta
 * @property {Roles[]} roles
 * @property {string[]} scopes
 * @property {string[]} featureFlags
 */

/**
 * @template T
 * @typedef {Object} GuardObject<T>
 * @property {T[]} [all] - Succeeds if all guards pass.
 * @property {T[]} [any] - Succeeds if any guard passes.
 */

/**
 * @template T
 * @typedef {T[] | GuardObject<T>} GuardAttribute<T>
 */

/**
 * @typedef {Object} RouteRules
 * @property {GuardAttribute<Roles>} [roles] - Restrict page access based on user roles.
 * @property {GuardAttribute<string>} [scopes] - Restrict page access based on user scopes.
 * @property {GuardAttribute<string>} [featureFlags] - Restrict page access based on user feature flags.
 * @property {GuardAttribute<RouteGuards>} [guards] - Set of guard rules by which we determine should the user be able to access the page.
 * @property {(meta: UserMeta) => boolean} [canAccess] - Custom restriction callback that is supplied with current user's access rights.
 */

/**
 * @param {string[]} haystack
 * @param {string[]} needles
 * @returns {boolean}
 */
const includesAll = (haystack, needles) =>
  needles.every((value) => haystack.includes(value));

/**
 *
 * @param {{ rules: RouteRules, patterns: string[]}} options
 * @param {string[]} patterns
 * @returns
 */
const applyRules = (options) => {
  const { rules, patterns } = options;

  return patterns.map((pattern) => ({
    ...rules,
    pattern,
  }));
};

const homepageRoutes = applyRules({
  patterns: ['/home'],
  rules: {
    canAccess: ({ roles, featureFlags }) => {
      if (
        roles.includes(Roles.InternalUser) ||
        roles.includes(Roles.SelfService) ||
        roles.includes(Roles.Scale) ||
        includesAll(roles, [Roles.ExternalUser, Roles.SupplyUser])
      ) {
        return true;
      }

      if (includesAll(roles, [Roles.ExternalUser, Roles.DemandUser])) {
        return (
          featureFlags.includes('demand_homepage') ||
          featureFlags.includes('demand_cc_mvp')
        );
      }

      return false;
    },
  },
});

const userRoutes = applyRules({
  patterns: ['/users', '/users/:uuid', '/users/:uuid/:tab?'],
  rules: {
    guards: [RouteGuards.MigrationTrial, RouteGuards.RestrictCRM],
    roles: [
      Roles.InternalUser,
      Roles.SelfService,
      Roles.DemandUser,
      Roles.Scale,
    ],
  },
});

const internalUserRoutes = applyRules({
  patterns: [
    '/accounts',
    '/analytics/self-service',
    '/billing/payable-adjustments',
    '/billing/payables',
    '/billing/payables/:id',
    '/billing/receivables-adjustments',
    '/billing/receivables',
    '/billing/receivables/:id',
    '/billing/tools/data-imports',
    '/billing/tools/imported-channel-events',
    '/billing/tools/imported-lead-events',
    '/channels/channels',
    '/channels/contracts',
    '/channels/template-center',
    '/channels/template-center/:uuid',
    '/channels/template-center/edit/:uuid',
    '/channels/template-center/new',
    '/channels/yield-optimizer',
    '/demand-sub-accounts',
    '/feature-flags',
    '/feature-flags/:uuid',
    '/feature-flags/:uuid',
    '/leads',
    '/leads/:uuid/:tab?',
    '/offer-management/credit-cards/benchmark-reports',
    '/offer-management/credit-cards/capping',
    '/offer-management/credit-cards/configurations',
    '/offer-management/credit-cards/contracts',
    '/offer-management/credit-cards/lead-filtering/matching-criteria',
    '/offer-management/credit-cards/lead-filtering/product-offer-rules',
    '/offer-management/credit-cards/product-offers',
    '/offer-management/credit-cards/product-offers/:uuid',
    '/offer-management/custom-offers/benchmark-reports',
    '/offer-management/custom-offers/configurations',
    '/offer-management/custom-offers/contracts',
    '/offer-management/custom-offers/lead-filtering/offer-rules',
    '/offer-management/custom-offers/offers',
    '/offer-management/life-insurance/benchmark-reports',
    '/offer-management/life-insurance/configurations',
    '/offer-management/life-insurance/contracts',
    '/offer-management/personal-loans/capping',
    '/offer-management/personal-loans/configurations',
    '/offer-management/personal-loans/contracts',
    '/offer-management/personal-loans/home',
    '/offer-management/personal-loans/lead-filtering/lender-provided-filters',
    '/offer-management/personal-loans/lead-filtering/preview-offer-rules',
    '/offer-management/personal-loans/preview-offers',
    '/offer-management/personal-loans/pricing-controls',
    '/offer-management/savings/benchmark-reports',
    '/offer-management/savings/capping',
    '/offer-management/savings/configurations',
    '/offer-management/savings/contracts',
    '/offer-management/savings/lead-filtering',
    '/offer-management/savings/product-offers',
    '/premium-lenders',
    '/supply-sub-accounts',
    '/whitelist',
  ],
  rules: {
    roles: [Roles.InternalUser],
  },
});

const offerRankingRoutes = applyRules({
  patterns: ['/demand/floors-and-ceilings'],
  rules: {
    featureFlags: ['enable_offer_ranking'], // Note - maps to hasEnableOfferRankingFeatureFlag
    roles: [Roles.InternalUser],
  },
});

const internalOrSupplyRoutes = applyRules({
  patterns: [
    '/channels/performance-summary',
    '/supply-sub-accounts/:uuid/:tab?',
  ],
  rules: {
    roles: [Roles.InternalUser, Roles.SupplyUser],
  },
});

const internalOrDemandRoutes = applyRules({
  patterns: ['/accounts/:uuid/:tab?', '/demand-sub-accounts/:uuid/:tab?'],
  rules: {
    roles: [Roles.InternalUser, Roles.DemandUser],
  },
});

const internalOrDemandAndBenchmarkReportsRoutes = applyRules({
  patterns: [
    '/benchmark-reports/:report?',
    '/offer-management/personal-loans/benchmark-reports',
  ],
  rules: {
    featureFlags: ['benchmark_reports'],
    roles: [Roles.InternalUser, Roles.DemandUser],
  },
});

const internalOrDemandAndMciReportsRoutes = applyRules({
  patterns: [
    Route.MciReports,
    Route.OfferManagement_PersonalLoans_MciOffersReports,
    Route.OfferManagement_PersonalLoans_MciRejectionsReports,
    Route.OfferManagement_PersonalLoans_MciConversionsReports,
  ],
  rules: {
    featureFlags: ['mci_reports'],
    roles: [Roles.InternalUser, Roles.DemandUser],
  },
});

const supplyOnlyRoutes = applyRules({
  patterns: ['/supply-migration'],
  rules: {
    roles: [Roles.SupplyUser, Roles.Reporter],
  },
});

const demandMvpRoutes = applyRules({
  patterns: [
    '/demand-sub-accounts/:uuid/contracts/:id?',
    '/demand/invoices',
    '/offer-management/custom-offers',
    '/offer-management/financial-services/auto-loan-refi',
    '/offer-management/financial-services/credit-cards',
    '/offer-management/financial-services/life-insurance',
    '/offer-management/financial-services/personal-loans/dashboard',
    '/offer-management/financial-services/personal-loans/data-and-analytics/benchmark-reports/:report?',
    '/offer-management/financial-services/savings',
    '/offer-management/financial-services/student-loan-refi',
    DemandMVPOfferManagementRoute.PersonalLoansMCIConversionsReports,
    DemandMVPOfferManagementRoute.PersonalLoansMCIOffersReports,
    DemandMVPOfferManagementRoute.PersonalLoansMCIRejectionsReports,
  ],
  rules: {
    featureFlags: ['demand_cc_mvp'],
    roles: [Roles.DemandUser],
  },
});

const selfServiceCrmAndAboveRoutes = applyRules({
  patterns: ['/performance-summary'],
  rules: {
    guards: [RouteGuards.OnboardingFlow],
    roles: [Roles.SelfService, Roles.Scale],
  },
});

const selfServiceRoutes = applyRules({
  patterns: ['/account/upgrade', '/account/upgrade-scale'],
  rules: {
    guards: [RouteGuards.RestrictCRM],
    roles: [Roles.SelfService],
  },
});

const selfServiceOnboardingRoutes = applyRules({
  patterns: ['/welcome', '/welcome/:step'],
  rules: {
    featureFlags: ['onboarding'],
    guards: [RouteGuards.RestrictCRM],
    roles: [Roles.SelfService],
  },
});

const selfServiceScaleRoutes = applyRules({
  patterns: ['/account/:tab?', '/purchase-plan'],
  rules: {
    guards: [RouteGuards.RestrictCRM],
    roles: [Roles.SelfService, Roles.Scale],
  },
});

const selfServiceScaleOnboardingRoutes = applyRules({
  patterns: [
    '/compliance',
    '/financial-services/custom-offers',
    '/financial-services/life-insurance',
    '/financial-services/student-loan-refi',
    '/financial-services/credit-cards',
    '/financial-services/savings',
    '/financial-services/auto-loan-refi',
    '/invoices',
  ],
  rules: {
    guards: [RouteGuards.OnboardingFlow, RouteGuards.RestrictCRM],
    roles: [Roles.SelfService, Roles.Scale],
  },
});

const selfServiceScalePlanRoutes = applyRules({
  patterns: ['/financial-services/personal-loans', '/implementation-fee'],
  rules: {
    guards: [RouteGuards.PlanTrial, RouteGuards.RestrictCRM],
    roles: [Roles.SelfService, Roles.Scale],
  },
});

const selfServiceGuideRoutes = applyRules({
  patterns: ['/guide/:path?'],
  rules: {
    featureFlags: [
      'guide_users',
      'guide_integrations',
      'guide_performance_summary',
      'guide_homepage',
    ],
    roles: [Roles.SelfService],
  },
});

module.exports = [
  ...homepageRoutes,
  ...userRoutes,
  ...internalUserRoutes,
  ...internalOrSupplyRoutes,
  ...internalOrDemandRoutes,
  ...internalOrDemandAndBenchmarkReportsRoutes,
  ...internalOrDemandAndMciReportsRoutes,
  ...supplyOnlyRoutes,
  ...demandMvpRoutes,
  ...selfServiceCrmAndAboveRoutes,
  ...selfServiceRoutes,
  ...selfServiceOnboardingRoutes,
  ...selfServiceScaleRoutes,
  ...selfServiceScaleOnboardingRoutes,
  ...selfServiceScalePlanRoutes,
  ...selfServiceGuideRoutes,
  ...offerRankingRoutes,
];
